import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import NitInput, { validateNit, calcVerificationDigit } from "./components/NitInput";
import FormProgress from "./components/FormProgress";
import ConfirmationModal from "./components/ConfirmationModal";
import CitySelect from "./components/CitySelect";
import HelpPanel from "./components/HelpPanel";



const INITIAL_FORM = {
  nit: "",
  verificationDigit: "",
  organizationName: "",
  contactName: "",
  email: "",
  password: "",
  phone: "",
  address: "",
  city: "",
  acceptTerms: false,
};

const FORM_STEPS = [
  { label: "Empresa" },
  { label: "Contacto" },
  { label: "Opcional" },
];

export default function UserRegistration() {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [serverError, setServerError] = useState("");

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: "" }));
  };

  const completedFields = useMemo(() => {
    let count = 0;
    if (form?.nit && validateNit(form?.nit)?.valid) count++;
    if (form?.verificationDigit !== "" && parseInt(form?.verificationDigit) === calcVerificationDigit(form?.nit)) count++;
    if (form?.organizationName?.trim()?.length >= 3) count++;
    if (form?.contactName?.trim()?.length >= 3) count++;
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(form?.email)) count++;
    if (form?.password?.length >= 8) count++;
    if (form?.phone) count++;
    if (form?.address) count++;
    if (form?.city) count++;
    return count;
  }, [form]);

  const currentStep = useMemo(() => {
    if (!form?.nit && !form?.organizationName) return 0;
    if (!form?.contactName && !form?.email) return 1;
    return 2;
  }, [form]);

  const stepsWithCompletion = FORM_STEPS?.map((step, i) => ({
    ...step,
    completed: i === 0
      ? (form?.nit && validateNit(form?.nit)?.valid && form?.organizationName?.trim()?.length >= 3)
      : i === 1
        ? (form?.contactName?.trim()?.length >= 3 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(form?.email) && form?.password?.length >= 8)
        : false,
  }));

  const validate = () => {
    const newErrors = {};
    const nitValidation = validateNit(form?.nit);
    if (!form?.nit) newErrors.nit = "El NIT es requerido";
    else if (!nitValidation?.valid) newErrors.nit = nitValidation?.message;
    else if (form?.verificationDigit === "") newErrors.nit = "Ingrese el d\u00edgito de verificaci\u00f3n";
    else if (parseInt(form?.verificationDigit) !== calcVerificationDigit(form?.nit)) newErrors.nit = `D\u00edgito de verificaci\u00f3n incorrecto. El correcto es: ${calcVerificationDigit(form?.nit)}`;

    if (!form?.organizationName?.trim()) newErrors.organizationName = "La raz\u00f3n social es requerida";
    else if (form?.organizationName?.trim()?.length < 3) newErrors.organizationName = "M\u00ednimo 3 caracteres";

    if (!form?.contactName?.trim()) newErrors.contactName = "El nombre del contacto es requerido";
    else if (form?.contactName?.trim()?.length < 3) newErrors.contactName = "M\u00ednimo 3 caracteres";

    if (!form?.email) newErrors.email = "El correo electr\u00f3nico es requerido";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(form?.email)) newErrors.email = "Correo electr\u00f3nico inv\u00e1lido";

    if (!form?.password) newErrors.password = "La contrase\u00f1a es requerida";
    else if (form?.password?.length < 8) newErrors.password = "M\u00ednimo 8 caracteres";

    if (form?.phone && !/^[0-9+\s\-()]{7,15}$/?.test(form?.phone)) newErrors.phone = "N\u00famero de tel\u00e9fono inv\u00e1lido";

    if (!form?.acceptTerms) newErrors.acceptTerms = "Debe aceptar los t\u00e9rminos y condiciones";

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setServerError("");
    await new Promise(r => setTimeout(r, 1800));
    setLoading(false);
    setShowConfirmation(true);
  };

  const handleClear = () => {
    setForm(INITIAL_FORM);
    setErrors({});
    setServerError("");
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    setForm(INITIAL_FORM);
    setErrors({});
    navigate('/login');
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--color-background)" }}>
      {/* Top Navigation Bar */}
      <header className="sticky top-0 z-40 border-b" style={{ background: "var(--color-card)", borderColor: "var(--color-border)", boxShadow: "var(--shadow-sm)" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: "var(--color-primary)" }}>
              <Icon name="Package" size={20} color="#fff" strokeWidth={2.5} />
            </div>
            <div>
              <span className="text-base font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)" }}>Update-vital-Ftech</span>
              <span className="hidden sm:block text-xs" style={{ fontFamily: "var(--font-caption)", color: "var(--color-muted-foreground)" }}>Distribuci\u00f3n de Software</span>
            </div>
          </div>
          <nav className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate("/login")} iconName="LogIn" iconPosition="left">
              <span className="hidden md:inline">Iniciar Sesi\u00f3n</span>
            </Button>
          </nav>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Left: Info Panel */}
          <div className="lg:col-span-1 space-y-4">
            {/* Hero card */}
            <div className="rounded-xl p-5 md:p-6" style={{ background: "linear-gradient(135deg, var(--color-primary), var(--color-secondary))", color: "#fff" }}>
              <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: "rgba(255,255,255,0.2)" }}>
                <Icon name="Building2" size={24} color="#fff" />
              </div>
              <h1 className="text-xl md:text-2xl font-bold mb-2" style={{ fontFamily: "var(--font-heading)" }}>
                Registro de Empresa
              </h1>
              <p className="text-sm opacity-90" style={{ fontFamily: "var(--font-body)" }}>
                Solicite acceso a la plataforma de distribuci\u00f3n controlada de software para su organizaci\u00f3n colombiana.
              </p>
            </div>

            {/* Process steps */}
            <div className="rounded-xl p-4 md:p-5" style={{ background: "var(--color-card)", border: "1px solid var(--color-border)", boxShadow: "var(--shadow-sm)" }}>
              <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)" }}>
                Proceso de Registro
              </h3>
              <div className="space-y-3">
                {[
                  { icon: "ClipboardList", title: "Complete el formulario", desc: "Ingrese los datos de su empresa con NIT v\u00e1lido" },
                  { icon: "Clock", title: "Revisi\u00f3n administrativa", desc: "Un administrador revisar\u00e1 su solicitud en 1-3 d\u00edas h\u00e1biles" },
                  { icon: "Mail", title: "Notificaci\u00f3n por correo", desc: "Recibir\u00e1 un correo cuando su cuenta sea activada" },
                  { icon: "Download", title: "Acceso a actualizaciones", desc: "Descargue las actualizaciones asignadas a su empresa" },
                ]?.map((step, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "var(--color-primary)15" }}>
                      <Icon name={step?.icon} size={16} color="var(--color-primary)" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold" style={{ fontFamily: "var(--font-caption)", color: "var(--color-foreground)" }}>{step?.title}</p>
                      <p className="text-xs" style={{ fontFamily: "var(--font-caption)", color: "var(--color-muted-foreground)" }}>{step?.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Help panel */}
            <HelpPanel />

            {/* Trust signals */}
            <div className="rounded-xl p-4" style={{ background: "var(--color-card)", border: "1px solid var(--color-border)" }}>
              <div className="flex items-center gap-2 mb-3">
                <Icon name="Shield" size={16} color="var(--color-success)" />
                <span className="text-xs font-semibold" style={{ fontFamily: "var(--font-caption)", color: "var(--color-foreground)" }}>Seguridad y Cumplimiento</span>
              </div>
              <div className="space-y-2">
                {[
                  "Cumplimiento Ley 1581 de 2012 (Habeas Data)",
                  "Validaci\u00f3n NIT con algoritmo DIAN oficial",
                  "Datos cifrados con TLS 1.3",
                ]?.map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Icon name="CheckCircle" size={12} color="var(--color-success)" />
                    <span className="text-xs" style={{ fontFamily: "var(--font-caption)", color: "var(--color-muted-foreground)" }}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Registration Form */}
          <div className="lg:col-span-2">
            <div className="rounded-xl p-5 md:p-8" style={{ background: "var(--color-card)", border: "1px solid var(--color-border)", boxShadow: "var(--shadow-md)" }}>
              <div className="mb-6">
                <h2 className="text-xl md:text-2xl font-bold mb-1" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)" }}>
                  Solicitud de Acceso
                </h2>
                <p className="text-sm" style={{ fontFamily: "var(--font-body)", color: "var(--color-muted-foreground)" }}>
                  Complete todos los campos requeridos (<span style={{ color: "var(--color-error)" }}>*</span>) para enviar su solicitud.
                </p>
              </div>

              <FormProgress
                steps={stepsWithCompletion}
                currentStep={currentStep}
                completedFields={completedFields}
                totalFields={9}
              />

              {serverError && (
                <div className="mb-4 flex items-center gap-3 px-4 py-3 rounded-lg border" style={{ background: "rgba(220,38,38,0.08)", borderColor: "rgba(220,38,38,0.3)", color: "var(--color-error)" }}>
                  <Icon name="AlertCircle" size={16} color="currentColor" />
                  <span className="text-sm font-caption">{serverError}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                {/* Section 1: Company Info */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4 pb-2 border-b" style={{ borderColor: "var(--color-border)" }}>
                    <div className="w-6 h-6 rounded flex items-center justify-center" style={{ background: "var(--color-primary)" }}>
                      <Icon name="Building2" size={14} color="#fff" />
                    </div>
                    <h3 className="text-sm font-semibold" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)" }}>
                      Informaci\u00f3n de la Empresa
                    </h3>
                  </div>
                  <div className="space-y-4">
                    <NitInput
                      value={form?.nit}
                      verificationDigit={form?.verificationDigit}
                      onChange={(v) => handleChange("nit", v)}
                      onVerificationDigitChange={(v) => handleChange("verificationDigit", v)}
                      error={errors?.nit}
                    />
                    <Input
                      label="Raz\u00f3n Social / Nombre de la Empresa"
                      type="text"
                      placeholder="Ej: Tecnolog\u00edas Innovadoras S.A.S."
                      value={form?.organizationName}
                      onChange={(e) => handleChange("organizationName", e?.target?.value)}
                      error={errors?.organizationName}
                      required
                    />
                  </div>
                </div>

                {/* Section 2: Contact Info */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4 pb-2 border-b" style={{ borderColor: "var(--color-border)" }}>
                    <div className="w-6 h-6 rounded flex items-center justify-center" style={{ background: "var(--color-secondary)" }}>
                      <Icon name="User" size={14} color="#fff" />
                    </div>
                    <h3 className="text-sm font-semibold" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)" }}>
                      Datos del Contacto Principal
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Nombre Completo del Contacto"
                      type="text"
                      placeholder="Ej: Carlos Andr\u00e9s Rodr\u00edguez"
                      value={form?.contactName}
                      onChange={(e) => handleChange("contactName", e?.target?.value)}
                      error={errors?.contactName}
                      required
                    />
                    <Input
                      label="Correo Electr\u00f3nico Corporativo"
                      type="email"
                      placeholder="Ej: contacto@empresa.com.co"
                      value={form?.email}
                      onChange={(e) => handleChange("email", e?.target?.value)}
                      error={errors?.email}
                      required
                    />
                    <div className="md:col-span-2">
                      <Input
                        label="Contrase\u00f1a de Acceso"
                        type="password"
                        placeholder="M\u00ednimo 8 caracteres"
                        value={form?.password}
                        onChange={(e) => handleChange("password", e?.target?.value)}
                        error={errors?.password}
                        required
                      />
                      <p className="text-xs mt-1" style={{ fontFamily: "var(--font-caption)", color: "var(--color-muted-foreground)" }}>Esta ser\u00e1 tu contrase\u00f1a para ingresar al sistema una vez activada tu cuenta.</p>
                    </div>
                  </div>
                </div>

                {/* Section 3: Optional Info */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-4 pb-2 border-b" style={{ borderColor: "var(--color-border)" }}>
                    <div className="w-6 h-6 rounded flex items-center justify-center" style={{ background: "var(--color-muted)" }}>
                      <Icon name="MapPin" size={14} color="var(--color-muted-foreground)" />
                    </div>
                    <h3 className="text-sm font-semibold" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)" }}>
                      Informaci\u00f3n Adicional <span className="text-xs font-normal" style={{ color: "var(--color-muted-foreground)" }}>(Opcional)</span>
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      label="Tel\u00e9fono de Contacto"
                      type="tel"
                      placeholder="Ej: +57 601 234 5678"
                      value={form?.phone}
                      onChange={(e) => handleChange("phone", e?.target?.value)}
                      error={errors?.phone}
                    />
                    <CitySelect
                      value={form?.city}
                      onChange={(v) => handleChange("city", v)}
                      error={errors?.city}
                    />
                    <div className="md:col-span-2">
                      <Input
                        label="Direcci\u00f3n de la Empresa"
                        type="text"
                        placeholder="Ej: Cra. 7 # 32-16, Piso 5, Bogot\u00e1 D.C."
                        value={form?.address}
                        onChange={(e) => handleChange("address", e?.target?.value)}
                        error={errors?.address}
                      />
                    </div>
                  </div>
                </div>

                {/* Terms */}
                <div className="mb-6 p-4 rounded-lg" style={{ background: "var(--color-muted)", border: "1px solid var(--color-border)" }}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form?.acceptTerms}
                      onChange={(e) => handleChange("acceptTerms", e?.target?.checked)}
                      className="mt-0.5 w-4 h-4 rounded flex-shrink-0"
                      style={{ accentColor: "var(--color-primary)" }}
                    />
                    <span className="text-xs" style={{ fontFamily: "var(--font-caption)", color: "var(--color-foreground)" }}>
                      Acepto los{" "}
                      <span className="underline cursor-pointer" style={{ color: "var(--color-primary)" }}>T\u00e9rminos y Condiciones</span>
                      {" "}y la{" "}
                      <span className="underline cursor-pointer" style={{ color: "var(--color-primary)" }}>Pol\u00edtica de Privacidad</span>
                      {" "}de Update-vital-Ftech, incluyendo el tratamiento de datos personales conforme a la Ley 1581 de 2012.
                    </span>
                  </label>
                  {errors?.acceptTerms && (
                    <p className="text-xs mt-2 flex items-center gap-1" style={{ color: "var(--color-error)", fontFamily: "var(--font-caption)" }}>
                      <Icon name="AlertCircle" size={12} color="var(--color-error)" />
                      {errors?.acceptTerms}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    loading={loading}
                    iconName="Send"
                    iconPosition="left"
                    className="flex-1"
                  >
                    {loading ? "Enviando solicitud..." : "Enviar Solicitud de Registro"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    onClick={handleClear}
                    iconName="RotateCcw"
                    iconPosition="left"
                    disabled={loading}
                  >
                    Limpiar
                  </Button>
                </div>

                {/* Status note */}
                <div className="mt-4 flex items-start gap-2 p-3 rounded-lg" style={{ background: "rgba(217,119,6,0.06)", border: "1px solid rgba(217,119,6,0.2)" }}>
                  <Icon name="Info" size={16} color="var(--color-warning)" />
                  <p className="text-xs" style={{ fontFamily: "var(--font-caption)", color: "var(--color-foreground)" }}>
                    Su organizaci\u00f3n y usuario ser\u00e1n creados en estado <strong>PENDIENTE</strong>. Recibir\u00e1 una notificaci\u00f3n por correo electr\u00f3nico una vez que un administrador active su cuenta.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>
      {/* Footer */}
      <footer className="mt-10 border-t py-6" style={{ borderColor: "var(--color-border)", background: "var(--color-card)" }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded flex items-center justify-center" style={{ background: "var(--color-primary)" }}>
              <Icon name="Package" size={12} color="#fff" />
            </div>
            <span className="text-xs font-semibold" style={{ fontFamily: "var(--font-heading)", color: "var(--color-foreground)" }}>Update-vital-Ftech</span>
          </div>
          <p className="text-xs" style={{ fontFamily: "var(--font-caption)", color: "var(--color-muted-foreground)" }}>
            &copy; {new Date()?.getFullYear()} Update-vital-Ftech. Todos los derechos reservados. Colombia.
          </p>
          <div className="flex items-center gap-1">
            <Icon name="Shield" size={12} color="var(--color-success)" />
            <span className="text-xs" style={{ fontFamily: "var(--font-caption)", color: "var(--color-muted-foreground)" }}>Ley 1581 de 2012</span>
          </div>
        </div>
      </footer>
      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={handleConfirmationClose}
        formData={form}
      />
    </div>
  );
}